import type { AccessTokenProvider } from "~application/auth/ports/access-token-provider.ts";
import type { AuthenticationRepository } from "~application/auth/ports/authentication-repository.ts";
import type { ApplianceRepository } from "~application/devices/ports/appliance-repository.ts";
import type { ModulatorRepository } from "~application/devices/ports/modulator-repository.ts";
import type { PilotRepository } from "~application/devices/ports/pilot-repository.ts";
import type { ThirdPartySensorRepository } from "~application/devices/ports/third-party-sensor-repository.ts";
import type { InstallTestsRepository } from "~application/install-tests/ports/install-tests-repository.ts";
import type { SitePhotoRepository } from "~application/sites/ports/site-photo-repository.ts";
import type { SiteRepository } from "~application/sites/ports/site-repository.ts";
import type {
  InstallTestV2V5,
  InstallTestV2V5Result,
} from "~domain/install-tests/models/v2v5/install-test-v2v5-model.ts";
import type { InstallTestVx, InstallTestVxResult } from "~domain/install-tests/models/vx/install-test-vx-model.ts";
import type { InstallTestService } from "~domain/install-tests/services/install-test-service.ts";
import type { TAppConfig } from "~ui/@application/configs/config-schema.ts";

import { BrandService } from "~domain/brands/services/brand-service.ts";
import { V2V5InstallTestService } from "~domain/install-tests/services/impl/v2v5-install-test-service.ts";
import { VxInstallTestService } from "~domain/install-tests/services/impl/vx-install-test-service.ts";
import {
  type BackofficeSdkClient,
  makeSdkBackofficeClient,
} from "~infrastructure/api/backoffice/http/backoffice-sdk-client.ts";
import {
  type BackofficeApi,
  createBackofficeTsRestClient,
} from "~infrastructure/api/backoffice/http/backoffice-tsrest-client.ts";
import { BackofficeAuthenticationRepository } from "~infrastructure/auth/impl/backoffice-authentication-repository.ts";
import { LocalStorageAccessTokenProvider } from "~infrastructure/auth/impl/local-storage-access-token-provider.ts";
import { BackofficeApplianceRepository } from "~infrastructure/devices/impl/backoffice-appliance-repository.ts";
import { BackofficeModulatorRepository } from "~infrastructure/devices/impl/backoffice-modulator-repository.ts";
import { BackofficePilotRepository } from "~infrastructure/devices/impl/backoffice-pilot-repository.ts";
import { BackofficeThirdPartySensorRepository } from "~infrastructure/devices/impl/backoffice-third-party-sensor-repository.ts";
import { BackofficeInstallTestsRepository } from "~infrastructure/install-tests/impl/backoffice-install-tests-repository.ts";
import { BackofficeSitePhotoRepository } from "~infrastructure/sites/impl/backoffice-site-photo-repository.ts";
import { BackofficeSiteRepository } from "~infrastructure/sites/impl/backoffice-site-repository.ts";

export type TServices = {
  api: {
    backoffice: BackofficeApi;
    backofficeSdk: BackofficeSdkClient;
  };
  repositories: {
    tokenProvider: AccessTokenProvider;
    authenticationRepository: AuthenticationRepository;
    applianceRepository: ApplianceRepository;
    modulatorRepository: ModulatorRepository;
    pilotRepository: PilotRepository;
    installTestsRepository: InstallTestsRepository;
    thirdPartySensorRepository: ThirdPartySensorRepository;
    siteRepository: SiteRepository;
    sitePhotoRepository: SitePhotoRepository;
  };
  services: {
    brandService: BrandService;
    installTestV2V5Service: InstallTestService<InstallTestV2V5, InstallTestV2V5Result>;
    installTestVxService: InstallTestService<InstallTestVx, InstallTestVxResult>;
  };
};

export async function createServices(
  config: TAppConfig,
  overrides: Partial<TServices["repositories"]> = {},
): Promise<TServices> {
  const localStorageTokenProvider = new LocalStorageAccessTokenProvider();

  const backoffice = createBackofficeTsRestClient(config, localStorageTokenProvider);

  const backofficeSdkClient = makeSdkBackofficeClient(config, localStorageTokenProvider);

  const authenticationRepository =
    overrides.authenticationRepository ?? new BackofficeAuthenticationRepository(config, backoffice);

  const modulatorRepository = overrides.modulatorRepository ?? new BackofficeModulatorRepository(backoffice);
  const applianceRepository = overrides.applianceRepository ?? new BackofficeApplianceRepository(backofficeSdkClient);

  const siteRepository =
    overrides.siteRepository ?? new BackofficeSiteRepository(backofficeSdkClient, backoffice, modulatorRepository);

  const pilotRepository = overrides.pilotRepository ?? new BackofficePilotRepository(backoffice);

  const installTestsRepository =
    overrides.installTestsRepository ?? new BackofficeInstallTestsRepository(backoffice, siteRepository);

  const thirdPartySensorRepository =
    overrides.thirdPartySensorRepository ?? new BackofficeThirdPartySensorRepository(backoffice);

  const installTestV2V5Service: InstallTestService<InstallTestV2V5, InstallTestV2V5Result> =
    new V2V5InstallTestService();
  const installTestVxService: InstallTestService<InstallTestVx, InstallTestVxResult> = new VxInstallTestService();
  const brandService = new BrandService();

  const sitePhotoRepository: SitePhotoRepository =
    overrides.sitePhotoRepository ?? new BackofficeSitePhotoRepository(backoffice);

  return {
    api: {
      backoffice: backoffice,
      backofficeSdk: backofficeSdkClient,
    },
    repositories: {
      tokenProvider: localStorageTokenProvider,
      authenticationRepository: authenticationRepository,
      applianceRepository: applianceRepository,
      modulatorRepository: modulatorRepository,
      pilotRepository: pilotRepository,
      installTestsRepository: installTestsRepository,
      thirdPartySensorRepository: thirdPartySensorRepository,
      siteRepository: siteRepository,
      sitePhotoRepository: sitePhotoRepository,
    },
    services: {
      installTestV2V5Service: installTestV2V5Service,
      installTestVxService: installTestVxService,
      brandService: brandService,
    },
  };
}
